import styles from "./MarketSwitcher.module.css";
import { MARKETS, getMarket, chooseMarket, marketsEnabled, marketInfo, marketCurrency, unprefixedStorePath } from "../../utils/market";
import { track, EVENTS } from "../../utils/analyticsService";
import { SYMBOLS, currencyType, currencyCode } from "../../utils/currency";

/**
 * The store picker. One native select, so every variant opens the same list:
 *
 * - "row" (default): "Shopping in: 🇺🇸 United States", in the footer and on the marketplace.
 * - "chip": "🇬🇧 $" in the top bar, the select laid invisibly over it.
 * - "link": a "Change" link (the store notice), the select laid over it.
 *
 * Shown only once markets are on (the server switch, or a ?market= preview
 * tab); India-only visitors never see it. A change reloads, so every price on
 * the page is re-asked in the new store.
 */
export const MarketSwitcher = ({ className, variant = "row", label = "Change" }) => {
  if (!marketsEnabled()) return null;
  const current = getMarket();
  const info = marketInfo(current);
  // The currency this store's prices came back in, once a page has loaded some.
  const currency = marketCurrency(current);
  // The listed stores, plus this visitor's own country when it isn't one of them.
  const stores = MARKETS[current] ? Object.entries(MARKETS) : [[current, info], ...Object.entries(MARKETS)];

  const select = (cls) => (
    <select
      className={cls}
      value={current}
      aria-label="Choose your country's store"
      onChange={(e) => {
        const next = e.target.value;
        if (next === current) return;
        track(EVENTS.MARKET_CHANGED, { from: current, to: next, via: variant });
        chooseMarket(next);
        // A store's own page (/us, /us/category/x, /us/brand/steam) would open
        // its store again on reload: go to the same page in the chosen store.
        const plain = unprefixedStorePath();
        if (plain) window.location.assign(plain);
        else window.location.reload();
      }}
    >
      {stores.map(([code, m]) => (
        <option key={code} value={code}>
          {m.flag} {m.label}
        </option>
      ))}
    </select>
  );

  if (variant === "chip") {
    return (
      <label className={`${styles.chip} ${className || ""}`} title={`${info.label} store, prices in ${currencyCode(currency)}`}>
        <span aria-hidden="true">
          {info.flag} {SYMBOLS[currencyType(currency)] || currencyCode(currency)}
        </span>
        {select(styles.overlay)}
      </label>
    );
  }

  if (variant === "link") {
    return (
      <label className={`${styles.link} ${className || ""}`}>
        <span aria-hidden="true">{label}</span>
        {select(styles.overlay)}
      </label>
    );
  }

  return (
    <label className={`${styles.switcher} ${className || ""}`}>
      <span className={styles.label}>Shopping in</span>
      {select(styles.select)}
    </label>
  );
};

export default MarketSwitcher;
